import React, { useEffect } from "react";
import DOMPurify from "dompurify";

// Permissive sanitizer config: SEO Custom Head HTML may legitimately contain
// JSON-LD <script> tags, verification meta, link/preconnect tags, etc.
// We allow these but DOMPurify still strips inline event handlers (onerror,
// onload, etc.) defending against pasted-content XSS without breaking the
// admin-only feature.
const SANITIZE_CONFIG = {
  ADD_TAGS: ["script", "link", "meta", "noscript"],
  ADD_ATTR: ["async", "defer", "type", "src", "rel", "href", "crossorigin", "referrerpolicy", "integrity", "property", "name", "content"],
  FORCE_BODY: true,
  WHOLE_DOCUMENT: false,
};

export default function SeoHead({ seo }) {
  useEffect(() => {
    if (!seo) return;
    if (seo.page_title) document.title = seo.page_title;

    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      Object.entries(attrs.set || {}).forEach(([k, v]) => el.setAttribute(k, v));
    };
    const ensureLink = (rel, href) => {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    ensureMeta('meta[name="description"]', { create: { name: "description" }, set: { content: seo.meta_description || "" } });
    ensureMeta('meta[name="keywords"]', { create: { name: "keywords" }, set: { content: seo.keywords || "" } });
    ensureMeta('meta[name="robots"]', { create: { name: "robots" }, set: { content: seo.robots || "index, follow" } });
    ensureMeta('meta[property="og:title"]', { create: { property: "og:title" }, set: { content: seo.og_title || seo.page_title || "" } });
    ensureMeta('meta[property="og:description"]', { create: { property: "og:description" }, set: { content: seo.og_description || seo.meta_description || "" } });
    if (seo.og_image) {
      ensureMeta('meta[property="og:image"]', { create: { property: "og:image" }, set: { content: seo.og_image } });
    }
    ensureMeta('meta[name="twitter:card"]', { create: { name: "twitter:card" }, set: { content: "summary_large_image" } });
    ensureLink("canonical", seo.canonical_url);

    // Inject sanitized custom head HTML
    const HEAD_MARKER = "data-sauderton-head-inject";
    document.head.querySelectorAll(`[${HEAD_MARKER}]`).forEach((n) => n.remove());
    if (seo.head_html && seo.head_html.trim()) {
      const safeHtml = DOMPurify.sanitize(seo.head_html, SANITIZE_CONFIG);
      const wrapper = document.createElement("div");
      wrapper.innerHTML = safeHtml;
      Array.from(wrapper.childNodes).forEach((node) => {
        if (node.nodeType !== 1) return;
        // Re-create script tags so they actually execute
        if (node.tagName === "SCRIPT") {
          const s = document.createElement("script");
          for (const a of node.attributes) s.setAttribute(a.name, a.value);
          s.text = node.textContent || "";
          s.setAttribute(HEAD_MARKER, "1");
          document.head.appendChild(s);
        } else {
          node.setAttribute(HEAD_MARKER, "1");
          document.head.appendChild(node);
        }
      });
    }
  }, [seo]);
  return null;
}
